import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Trash2, LayoutGrid } from "lucide-react";
import { modulBul } from "@/lib/moduller";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const sayfaIkon = (path) => {
  const m = modulBul(path);
  if (!m) return LayoutGrid;
  return (m.tabs.find((t) => path === t.to) || m.tabs.find((t) => t.to !== "/" && path.startsWith(t.to)) || m).icon;
};

export const PageHeader = ({ title, desc, children, icon }) => {
  const { pathname } = useLocation();
  const Ikon = icon || sayfaIkon(pathname);
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-white/10 fade-up">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 shrink-0 rounded-lg border border-white/15 bg-white/5 flex items-center justify-center"><Ikon className="w-5 h-5 text-muted-foreground" /></div>
        <div className="min-w-0">
          <h1 className="font-head text-xl sm:text-2xl font-bold tracking-tight text-[var(--g1)] truncate" data-testid="page-title">{title}</h1>
          {desc && <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">{desc}</p>}
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">{children}</div>
    </div>
  );
};

export const GlassCard = ({ className = "", children, ...rest }) => (
  <div className={`glass rounded-2xl ${className}`} {...rest}>{children}</div>
);

export const StatCard = ({ label, value, icon: Icon, hint, testid, delay = 0 }) => (
  <GlassCard className="p-5 fade-up hover:-translate-y-0.5 transition-transform duration-300" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      {Icon && <div className="w-9 h-9 rounded-xl grad-bg flex items-center justify-center glow"><Icon className="w-4 h-4 text-white" /></div>}
    </div>
    <div className="font-head text-3xl font-bold mt-3">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </GlassCard>
);

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const EmptyState = ({ text, testid }) => (
  <div className="py-14 text-center text-sm text-muted-foreground" data-testid={testid}>{text}</div>
);

export const ConfirmDelete = ({ onConfirm, testid, title = "Kaydı sil" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testid} onClick={() => setOpen(true)}
        className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors">
        <Trash2 className="w-4 h-4" />
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="glass">
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz. Devam etmek istiyor musunuz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" className="bg-red-600 hover:bg-red-700" onClick={onConfirm}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const GradButton = ({ children, className = "", ...rest }) => (
  <button {...rest}
    className={`grad-bg glow text-white font-semibold text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-2 hover:brightness-110 active:scale-[0.98] transition-[filter,transform] disabled:opacity-50 ${className}`}>
    {children}
  </button>
);

export const TIP_ETIKET = {
  hammadde: "Hammadde", yari_mamul: "Yarı Mamul", mamul: "Mamul", ambalaj: "Ambalaj", hizmet: "Hizmet",
  fabrika: "Fabrika", depo: "Depo", ambalaj_deposu: "Ambalaj Deposu", geri_donusum: "Geri Dönüşüm", magaza: "Mağaza",
  karantina: "Karantina", sevkiyat: "Sevkiyat", uretim: "Üretim", montaj: "Montaj", kalite: "Kalite", paketleme: "Paketleme",
  admin: "Admin", yonetici: "Yönetici", satis: "Satış", depo_rol: "Depo", muhasebe: "Muhasebe", ik: "İK", bayi: "Bayi",
};

export const fmtMoney = (v, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(v || 0);
